import React from 'react';
import { IExerciseInstance } from '../../../types/workout/exercise-instance';
import { getYouTubeThumbnail } from '../../../utils/UtilFunctions';
import { PD, SF, SFm } from './tokens';
import { muscleGroupLabel, nonEmpty } from './helpers';
import PrescriptionChips from './PrescriptionChips';
import CompactSetTable from './CompactSetTable';
import RpeChip from './RpeChip';
import StatusPill from './StatusPill';

interface ExerciseCardProps {
  exercise: IExerciseInstance;
  groupIdx: number;
  groupName: string;
  posInGroup: number;
  totalInGroup: number;
  completedView: boolean;
  rpeLabel?: string;
  onPlay: (_multimedia: string) => void;
  cardRef?: (_el: HTMLDivElement | null) => void;
}

const ExerciseCard: React.FC<ExerciseCardProps> = ({
  exercise,
  groupIdx,
  groupName,
  posInGroup,
  totalInGroup,
  completedView,
  rpeLabel,
  onPlay,
  cardRef
}) => {
  const hasSetConfig = Boolean(exercise.setConfiguration && exercise.setConfiguration.length > 0);
  const completed = Boolean(exercise.completed);
  const notAsPlanned = Boolean(exercise.completedNotAsPlanned);
  const thumb = getYouTubeThumbnail(exercise.exercise?.multimedia || '');
  const muscleLabel = muscleGroupLabel(exercise.exercise);

  const statusPill = completedView ? (
    completed ? (
      <StatusPill tone="success" icon="pi-check" size="sm">
        Hecho
      </StatusPill>
    ) : notAsPlanned ? (
      <StatusPill tone="warn" icon="pi-exclamation-triangle" size="sm">
        Ajustes
      </StatusPill>
    ) : (
      <StatusPill tone="danger" icon="pi-times" size="sm">
        No hecho
      </StatusPill>
    )
  ) : null;

  return (
    <div
      ref={cardRef}
      className="pd-card"
      style={{
        width: 268,
        flex: '0 0 268px',
        background: PD.card,
        borderRadius: 16,
        border: `0.5px solid ${PD.cardEdge}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Thumbnail */}
      <div
        className="pd-thumb"
        onClick={() => onPlay(exercise.exercise?.multimedia || '')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onPlay(exercise.exercise?.multimedia || '');
        }}
      >
        {thumb && <img src={thumb} alt="" className="pd-thumb-img" />}

        {/* group + position badge */}
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            padding: '3px 8px',
            borderRadius: 999,
            background: PD.cardTranslucent,
            fontFamily: SF,
            fontSize: 10.5,
            fontWeight: 600,
            color: PD.ink,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            zIndex: 2
          }}
        >
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: PD.cta,
              color: PD.ctaFg,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: SFm,
              fontSize: 8.5,
              fontWeight: 700
            }}
          >
            {groupIdx + 1}
          </span>
          {totalInGroup > 1 ? `${posInGroup + 1}/${totalInGroup}` : groupName}
        </div>

        {/* status pill */}
        {statusPill && <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}>{statusPill}</div>}

        {/* gradient overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.45))'
          }}
        />

        {/* play overlay */}
        <div className="pd-thumb-play">
          <i className="pi pi-play" style={{ fontSize: 14, color: PD.cta, marginLeft: 2 }} />
        </div>
      </div>

      {/* Body */}
      <div
        style={{
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          flex: 1,
          minHeight: 0
        }}
      >
        {/* Name */}
        <div>
          <div
            style={{
              fontFamily: SF,
              fontSize: 14.5,
              fontWeight: 600,
              color: PD.ink,
              letterSpacing: '-0.01em',
              lineHeight: 1.2
            }}
          >
            {exercise.exercise?.name}
          </div>
          {muscleLabel && (
            <div style={{ fontFamily: SF, fontSize: 11.5, color: PD.ink3, marginTop: 2 }}>{muscleLabel}</div>
          )}
        </div>

        {/* Prescription chips (only if no setConfig) */}
        {!hasSetConfig && <PrescriptionChips exercise={exercise} />}

        {/* SetConfiguration table (plan) */}
        {hasSetConfig && (
          <div>
            {completedView && exercise.setLogs && exercise.setLogs.length > 0 && (
              <div
                style={{
                  fontFamily: SF,
                  fontSize: 9.5,
                  fontWeight: 700,
                  color: PD.ink3,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  marginBottom: 4
                }}
              >
                Plan
              </div>
            )}
            <CompactSetTable rows={exercise.setConfiguration ?? []} plan />
          </div>
        )}

        {/* Set logs (real) */}
        {completedView && completed && exercise.setLogs && exercise.setLogs.length > 0 && (
          <div>
            <div
              style={{
                fontFamily: SF,
                fontSize: 9.5,
                fontWeight: 700,
                color: PD.successInk,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                marginBottom: 4
              }}
            >
              Real
            </div>
            <CompactSetTable rows={exercise.setLogs} plan={false} rpeLabel={rpeLabel} />
          </div>
        )}

        {/* Single-exercise RPE (no setLogs) */}
        {completedView &&
          completed &&
          nonEmpty(exercise.rpe) &&
          (!exercise.setLogs || exercise.setLogs.length === 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  fontFamily: SF,
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: PD.ink3,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase'
                }}
              >
                {rpeLabel || 'RPE'}
              </span>
              <RpeChip value={exercise.rpe ?? null} />
            </div>
          )}

        {/* Notes / comments */}
        {((!completedView && nonEmpty(exercise.notes)) || (completedView && nonEmpty(exercise.comments))) && (
          <div
            style={{
              marginTop: 'auto',
              padding: '8px 10px',
              background: PD.fill,
              borderRadius: 10,
              fontFamily: SF,
              fontSize: 11.5,
              color: PD.ink2,
              lineHeight: 1.4
            }}
          >
            <span style={{ color: PD.ink3, fontWeight: 600 }}>{completedView ? 'Cliente · ' : 'Coach · '}</span>
            {completedView ? exercise.comments : exercise.notes}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExerciseCard;
